import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { warnDev } from "@/lib/log";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertOctagon, RefreshCw, Trash2, Search } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ErrorLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client-errors", { params: { limit: 500 } });
      setLogs(data || []);
    } catch (e) {
      warnDev("client-errors fetch failed", e);
      toast.error("Impossible de charger les erreurs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const purge = async () => {
    if (!window.confirm("Supprimer définitivement toutes les erreurs ?")) return;
    try {
      const { data } = await api.delete("/client-errors");
      toast.success(`${data.deleted} entrée(s) supprimée(s)`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const filtered = logs.filter((l) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (l.message || "").toLowerCase().includes(f)
        || (l.context || "").toLowerCase().includes(f)
        || (l.source || "").toLowerCase().includes(f)
        || (l.url || "").toLowerCase().includes(f);
  });

  return (
    <div className="space-y-5" data-testid="error-logs-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-3xl text-slate-900 flex items-center gap-2">
            <AlertOctagon className="w-6 h-6 text-[#DC2626]" />
            Erreurs techniques
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Journal des erreurs JavaScript et d'intégration remontées par les postes (kiosque, TV, guichet, console).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="error-logs-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          {user?.role === "SUPER_ADMIN" && (
            <Button variant="outline" onClick={purge}
                    className="text-[#DC2626] border-[#DC2626]/40 hover:bg-red-50"
                    data-testid="error-logs-purge">
              <Trash2 className="w-4 h-4 mr-2" /> Purger tout
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)}
                   placeholder="Filtrer (message, contexte, source, URL)…"
                   className="pl-9" data-testid="error-logs-filter" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="error-logs-empty">
              {loading ? "Chargement…" : "Aucune erreur à afficher. Tout va bien."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Source</th>
                    <th className="text-left py-3 px-4">Contexte</th>
                    <th className="text-left py-3 px-4">Message</th>
                    <th className="text-left py-3 px-4">URL</th>
                    <th className="text-left py-3 px-4">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="border-t border-slate-100 align-top hover:bg-slate-50"
                        data-testid={`error-log-row-${l.id}`}>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{formatDate(l.created_at)}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-xs">
                          {l.source}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-700">{l.context || "—"}</td>
                      <td className="py-3 px-4 text-slate-900 max-w-xl">
                        <div className="font-medium">{l.message}</div>
                        {l.stack && (
                          <details className="mt-1">
                            <summary className="text-xs text-slate-500 cursor-pointer">Stack</summary>
                            <pre className="text-[10px] text-slate-500 mt-1 whitespace-pre-wrap break-all">{l.stack}</pre>
                          </details>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-xs">{l.url || "—"}</td>
                      <td className="py-3 px-4 text-slate-500 text-xs">{l.ip_address || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
